import { useCallback, useState, useMemo, useEffect } from 'react'

import { InputGroup, InputLeftAddon, Input } from '@wartek-id/input'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import FilterButtons from 'app/KinerjaShared/components/FilterButtons'
import { Header } from 'app/KinerjaShared/components/Header'
import Pagination from 'app/KinerjaShared/components/Pagination'
import TablePelaksanaan from './TablePelaksanaan'
import useBasePath from 'app/KinerjaTimPenilaiPenilikPage/hooks/useBasePath'
import usePelaksanaanList from 'app/KinerjaTimPenilaiPenilikPage/hooks/usePelaksanaanList'

import type {
  PelaksanaanFilterState,
  PelaksanaanFilter,
} from 'types/kinerja-tim-penilai-penilik'

const PAGE_SIZE = 10

const FILTER_STATE: Record<PelaksanaanFilterState, string> = {
  SEMUA: 'Semua',
  SELESAI_PRAKTIK_KINERJA: 'Selesai Praktik Kinerja',
  SELESAI_PERILAKU_KERJA: 'Selesai Perilaku Kerja',
  SELESAI_PENGEMBANGAN_KOMPETENSI: 'Selesai Pengembangan Kompetensi',
  SELESAI_SEMUA_KEGIATAN: 'Selesai Semua',
}

const mapping: Record<PelaksanaanFilterState, keyof PelaksanaanFilter> = {
  SEMUA: 'total',
  SELESAI_PRAKTIK_KINERJA: 'selesaiPraktikKinerja',
  SELESAI_PERILAKU_KERJA: 'selesaiPerilakuKerja',
  SELESAI_PENGEMBANGAN_KOMPETENSI: 'selesaiPengembanganKompetensi',
  SELESAI_SEMUA_KEGIATAN: 'selesaiSemuaKegiatan',
}

const Search = ({
  keyword,
  setKeyword,
}: {
  keyword: string
  setKeyword: (keyword: string) => void
}) => {
  const changeHandler = useCallback(
    (value: string) => setKeyword(value),
    [setKeyword]
  )

  return (
    <div className="max-w-[240px]">
      <InputGroup>
        <InputLeftAddon>
          <Icon>search</Icon>
        </InputLeftAddon>
        <Input
          size="sm"
          clearable
          placeholder="Cari"
          value={keyword}
          onChange={(e) => changeHandler(e.target.value)}
        />
      </InputGroup>
    </div>
  )
}

export default function DaftarPenilaianPelaksanaanPS() {
  const { basePath, isPPKPenilikUrl } = useBasePath()

  const [activeFilterState, setActiveFilterState] =
    useState<PelaksanaanFilterState>('SEMUA')
  const [keyword, setKeyword] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)

  const { summary, data, isLoading, isFetched } = usePelaksanaanList({
    filter: activeFilterState,
  })

  const listData = useMemo(() => data?.data ?? [], [data])
  const normalizedKeyword = useMemo(
    () => keyword.trim().toLowerCase(),
    [keyword]
  )
  const filteredData = useMemo(() => {
    if (!normalizedKeyword) return listData

    return listData.filter((item) =>
      item.namaPenilik?.toLowerCase().includes(normalizedKeyword)
    )
  }, [listData, normalizedKeyword])

  const totalItems = filteredData.length
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))
  const clampedPage = Math.min(Math.max(currentPage, 1), totalPages)
  const offset = (clampedPage - 1) * PAGE_SIZE
  const paginatedData = useMemo(
    () => filteredData.slice(offset, offset + PAGE_SIZE),
    [filteredData, offset]
  )

  useEffect(() => {
    if (currentPage !== clampedPage) {
      setCurrentPage(clampedPage)
    }
  }, [currentPage, clampedPage])

  const handleStateChange = useCallback((state: PelaksanaanFilterState) => {
    setActiveFilterState(state)
    setKeyword('')
    setCurrentPage(1)
  }, [])

  const handleKeywordChange = useCallback((newKeyword: string) => {
    setKeyword(newKeyword)
    setCurrentPage(1)
  }, [])

  return (
    <div className="px-24 py-6 m-auto max-w-7xl">
      <Header
        title={`${isPPKPenilikUrl ? 'Penilaian' : 'Pemantauan'} Kinerja`}
        className="mt-6"
      />
      <div className="mt-12 bg-white border rounded border-gray-30">
        <div className="items-center justify-between p-6 pb-0 space-y-2 xl:flex xl:space-y-0">
          <Text variant="heading-md" as="h2">
            Lakukan {isPPKPenilikUrl ? 'Penilaian' : 'Pemantauan'} serta cek
            status Praktik Kinerja dan Perilaku Kerja
          </Text>
          <Search keyword={keyword} setKeyword={handleKeywordChange} />
        </div>

        <div className="p-6">
          {isFetched && (
            <FilterButtons
              activeValue={activeFilterState}
              className="flex-wrap"
              onChange={handleStateChange}
              items={Object.keys(FILTER_STATE).map((state) => ({
                label: `${FILTER_STATE[state]} (${
                  summary[mapping[state as keyof typeof mapping]]
                })`,
                value: state as PelaksanaanFilterState,
              }))}
            />
          )}
        </div>

        <TablePelaksanaan
          basePath={basePath}
          data={paginatedData}
          isLoading={isLoading}
        />

        {totalItems > 0 && (
          <div className="p-4 border-t border-subdued">
            <Pagination
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              itemLabel="data"
              showItemCount
            />
          </div>
        )}
      </div>
    </div>
  )
}
